import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  IconButton,
  Button,
  CircularProgress,
  Avatar,
  Stack,
  Fade,
  Collapse,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from '@mui/icons-material';
import {
  Send as SendIcon,
  AutoAwesome as SparkleIcon,
  PlaylistPlay as StartReviewIcon,
} from '@mui/icons-material';
import axios from '../../../api/axios';
import MarkdownRenderer from '../../../components/Common/MarkdownRenderer';
import PageHeader from '../../../components/Common/PageHeader';
import ExerciseContent from '../../../components/Exercise/ExerciseContent';

function NotebookReviewPage() {
  const { notebookId } = useParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);

  const [notebookTitle, setNotebookTitle] = useState('');
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);
  const [chatConversationId, setChatConversationId] = useState(null);
  const [plan, setPlan] = useState(null);
  const [planExpanded, setPlanExpanded] = useState(true);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingContent]);

  useEffect(() => {
    let cancelled = false;
    axios.get(`/notes/${notebookId}`).then((res) => {
      if (!cancelled && res.data?.outline?.notebook_title) {
        setNotebookTitle(res.data.outline.notebook_title);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [notebookId]);

  const getBaseURL = () => {
    if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
    if (process.env.NODE_ENV === 'production') return '/api';
    const { protocol, hostname } = window.location;
    const port = 5001;
    if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
      return protocol === 'https:' ? '/api' : `${protocol}//${hostname}:${port}/api`;
    }
    return `${protocol}//localhost:${port}/api`;
  };

  // 根据 example 构建题目上下文（题干 + 选项 + 正确答案）
  const buildQuestionContext = (ex) => {
    let questionContext = ex?.question ? ex.question.trim().slice(0, 4000) : '';
    let optionsArr = ex?.options;
    if (optionsArr != null && typeof optionsArr === 'string') {
      try {
        optionsArr = JSON.parse(optionsArr);
      } catch (_) {
        optionsArr = null;
      }
    }
    if (Array.isArray(optionsArr) && optionsArr.length > 0) {
      const optionsText = optionsArr
        .map((opt, idx) => `${String.fromCharCode(65 + idx)}. ${(opt || '').trim().slice(0, 600)}`)
        .join('\n');
      questionContext += `\n【题目选项】\n${optionsText}\n`;
      const correct = ex?.correctAnswer ?? ex?.correct_answer;
      if (correct != null && String(correct).trim()) {
        questionContext += `【正确答案】${String(correct).trim()}\n`;
      }
    }
    return questionContext;
  };

  // 发送消息到笔记本聊天并流式追加 AI 回复；userMessageContent 为 null 时不追加用户消息气泡
  const sendChatRequest = async (messageToSend, userMessageContent) => {
    if (userMessageContent != null) {
      setMessages((prev) => [...prev, {
        id: `user-${Date.now()}`,
        role: 'user',
        type: 'text',
        content: userMessageContent,
      }]);
    }
    setSending(true);
    setStreamingContent('');
    const token = localStorage.getItem('token');
    const url = `${getBaseURL()}/notes/${notebookId}/chat`;
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token ? `Bearer ${token}` : '',
      },
      body: JSON.stringify({ message: messageToSend, conversationId: chatConversationId || undefined }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      setMessages((prev) => [...prev, {
        id: `err-${Date.now()}`,
        role: 'assistant',
        type: 'text',
        content: '抱歉，请求失败：' + (err.error || res.statusText),
      }]);
      setSending(false);
      return;
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let acc = '';
    let newConvId = chatConversationId;
    let fullText = '';
    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        const lines = acc.split('\n');
        acc = lines.pop() || '';
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.delta != null) {
                fullText += data.delta;
                setStreamingContent((c) => c + data.delta);
              }
              if (data.done && data.conversationId) {
                newConvId = data.conversationId;
              }
            } catch (e) { /* ignore */ }
          }
        }
      }
    } finally {
      if (newConvId != null) setChatConversationId(newConvId);
      setStreamingContent('');
      setMessages((prev) => [...prev, {
        id: `ast-${Date.now()}`,
        role: 'assistant',
        type: 'text',
        content: fullText.trim() || '无回复内容',
      }]);
      setSending(false);
    }
  };

  // 拉取并追加下一道复习题（不触发顶部「开始复习」的 loading）
  const appendNextReviewQuestion = async () => {
    try {
      const res = await axios.get(`/notebook-examples/notebook/${encodeURIComponent(notebookId)}/review/start`);
      const { action, exampleId, message, sectionTitle, plan: resPlan } = res.data;
      setPlan(resPlan || null);

      if (action === 'message_only') {
        setMessages((prev) => [...prev, {
          id: `msg-${Date.now()}`,
          role: 'assistant',
          type: 'text',
          content: message,
        }]);
        return;
      }

      if (action === 'do_question' && exampleId) {
        setMessages((prev) => [...prev, {
          id: `msg-${Date.now()}`,
          role: 'assistant',
          type: 'text',
          content: message + (sectionTitle ? `\n\n章节：${sectionTitle}` : ''),
        }]);
        try {
          const exRes = await axios.get(`/notebook-examples/${exampleId}`);
          const example = exRes.data;
          setMessages((prev) => [...prev, {
            id: `ex-${Date.now()}`,
            role: 'assistant',
            type: 'exercise',
            content: null,
            example,
            sectionTitle,
          }]);
        } catch (err) {
          setMessages((prev) => [...prev, {
            id: `msg-err-${Date.now()}`,
            role: 'assistant',
            type: 'text',
            content: '题目加载失败，请稍后重试。',
          }]);
        }
      }
    } catch (err) {
      setMessages((prev) => [...prev, {
        id: `msg-err-${Date.now()}`,
        role: 'assistant',
        type: 'text',
        content: err.response?.data?.error || '获取下一题失败，请稍后重试。',
      }]);
    }
  };

  // 复习页：用户提交答案后自动请求 AI 反馈；做对则夸奖并进入下一题，做错则安慰+讲解+询问是否要类似题
  const handleAnswerSubmitted = async (example, answer, result) => {
    const questionContext = buildQuestionContext(example);
    const isCorrect = result?.isCorrect;

    let instruction;
    if (isCorrect) {
      instruction = '请简短夸奖学生（一两句话，如：做得好、完全正确等），语气温暖。然后说明将进入下一题，鼓励继续。';
    } else {
      instruction = '请先安慰学生（如：差一点、别灰心、我们找到了你的问题、你在进步等），语气温暖。然后讲解本题关键点（为什么错、正确思路）。最后询问学生是否需要类似题目练习。格式要求：数学符号请直接使用 Unicode 字符，例如析取用 ∨、合取用 ∧、否定用 ¬，不要使用 LaTeX（如 \\vee、\\wedge），以免在页面显示时被拆行或错位。';
    }

    const messageToSend = questionContext
      ? `【当前正在做的题目】\n${questionContext}\n【用户刚提交的答案】\n${answer || '（无）'}\n【提交结果】${isCorrect ? '通过' : '未通过'}\n【/当前题目】\n\n${instruction}`
      : `用户刚提交了答案：${answer || '（无）'}，结果：${isCorrect ? '通过' : '未通过'}。\n\n${instruction}`;

    await sendChatRequest(messageToSend, null);

    if (isCorrect) {
      await appendNextReviewQuestion();
    }
  };

  const handleStartReview = async () => {
    setReviewLoading(true);
    try {
      const res = await axios.get(`/notebook-examples/notebook/${encodeURIComponent(notebookId)}/review/start`);
      const { action, exampleId, message, sectionTitle, plan: resPlan } = res.data;
      if (resPlan) setPlan(resPlan);
      else setPlan(null);

      if (action === 'message_only') {
        setMessages((prev) => [...prev, {
          id: `msg-${Date.now()}`,
          role: 'assistant',
          type: 'text',
          content: message,
        }]);
        setReviewLoading(false);
        return;
      }

      if (action === 'do_question' && exampleId) {
        setMessages((prev) => [...prev, {
          id: `msg-${Date.now()}`,
          role: 'assistant',
          type: 'text',
          content: message + (sectionTitle ? `\n\n章节：${sectionTitle}` : ''),
        }]);
        try {
          const exRes = await axios.get(`/notebook-examples/${exampleId}`);
          const example = exRes.data;
          setMessages((prev) => [...prev, {
            id: `ex-${Date.now()}`,
            role: 'assistant',
            type: 'exercise',
            content: null,
            example,
            sectionTitle,
          }]);
        } catch (err) {
          setMessages((prev) => [...prev, {
            id: `msg-err-${Date.now()}`,
            role: 'assistant',
            type: 'text',
            content: '题目加载失败，请稍后重试。',
          }]);
        }
      }
    } catch (err) {
      setMessages((prev) => [...prev, {
        id: `msg-err-${Date.now()}`,
        role: 'assistant',
        type: 'text',
        content: err.response?.data?.error || '请求失败，请稍后重试。',
      }]);
    } finally {
      setReviewLoading(false);
    }
  };

  const handleSendChat = async () => {
    const text = inputMessage.trim();
    if (!text || sending) return;

    const lastExercise = [...messages].reverse().find((m) => m.type === 'exercise' && m.example);
    const ex = lastExercise?.example;

    // 简答题/证明题：下方输入框内容视为作答，先提交再触发 AI 反馈
    if (lastExercise && ex?.id && (ex.questionType === 'short_answer' || ex.questionType === 'proof')) {
      setInputMessage('');
      setMessages((prev) => [...prev, {
        id: `user-${Date.now()}`,
        role: 'user',
        type: 'text',
        content: text,
      }]);
      setSending(true);
      try {
        const response = await axios.post(`/notebook-examples/${ex.id}/submit`, { answer: text.trim() });
        const submission = response.data.submission;
        await handleAnswerSubmitted(ex, text.trim(), {
          isCorrect: submission.status === 'passed',
          score: submission.score,
        });
      } catch (err) {
        setMessages((prev) => [...prev, {
          id: `err-${Date.now()}`,
          role: 'assistant',
          type: 'text',
          content: '提交失败：' + (err.response?.data?.error || err.message),
        }]);
      } finally {
        setSending(false);
      }
      return;
    }

    // 若当前对话中有「正在做的题目」，把题目（含代码）+ 选项 + 正确答案（如有）+ 用户已提交的答案与结果 作为上下文发给 AI，便于回答「为啥我是错的」等
    let questionContext = ex?.question ? ex.question.trim().slice(0, 4000) : '';
    let optionsArr = ex?.options;
    if (optionsArr != null && typeof optionsArr === 'string') {
      try {
        optionsArr = JSON.parse(optionsArr);
      } catch (_) {
        optionsArr = null;
      }
    }
    if (Array.isArray(optionsArr) && optionsArr.length > 0) {
      const optionsText = optionsArr
        .map((opt, idx) => `${String.fromCharCode(65 + idx)}. ${(opt || '').trim().slice(0, 600)}`)
        .join('\n');
      questionContext += `\n【题目选项】\n${optionsText}\n`;
      const correct = ex?.correctAnswer ?? ex?.correct_answer;
      if (correct != null && String(correct).trim()) {
        questionContext += `【正确答案】${String(correct).trim()}\n`;
      }
    }
    let submissionContext = '';
    if (ex?.id) {
      try {
        const questionId = `notebook_${ex.id}`;
        const subRes = await axios.get(`/submissions/question/${questionId}`);
        const submissions = subRes.data || [];
        const latest = submissions[0];
        if (latest) {
          let userAnswer = '';
          try {
            const parsed = typeof latest.code === 'string' ? JSON.parse(latest.code) : latest.code;
            if (parsed && typeof parsed.answer !== 'undefined') {
              userAnswer = String(parsed.answer);
            } else {
              userAnswer = typeof latest.code === 'string' ? latest.code.trim().slice(0, 800) : '';
            }
          } catch (_) {
            userAnswer = typeof latest.code === 'string' ? latest.code.trim().slice(0, 800) : '';
          }
          const resultText = latest.status === 'passed' ? '通过' : '未通过';
          submissionContext = `\n【用户已提交的答案】\n${userAnswer || '（无）'}\n【提交结果】${resultText}\n`;
        }
      } catch (_) { /* 忽略提交拉取失败 */ }
    }
    const messageToSend = questionContext
      ? `【当前正在做的题目】\n${questionContext}${submissionContext}【/当前题目】\n\n用户问题：${text}`
      : text;

    setInputMessage('');
    await sendChatRequest(messageToSend, text);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendChat();
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        maxHeight: '100vh',
        overflow: 'hidden',
        bgcolor: 'background.default',
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <PageHeader
          title={notebookTitle ? `复习 · ${notebookTitle}` : '复习的知识点'}
          backPath={() => navigate(`/questions/notebook/${notebookId}`)}
        />
      </Box>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            p: 2,
            bgcolor: 'grey.900',
            '&::-webkit-scrollbar': { width: 8 },
            '&::-webkit-scrollbar-thumb': { bgcolor: 'grey.700', borderRadius: 4 },
          }}
        >
          {messages.length === 0 && !streamingContent && (
            <Fade in>
              <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56, mx: 'auto', mb: 2 }}>
                  <SparkleIcon sx={{ fontSize: 32 }} />
                </Avatar>
                <Typography variant="h6" fontWeight={500} sx={{ mb: 1 }}>
                  复习的知识点
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  点击「开始复习」由 AI 根据你的学习状态选题；题目会在本页内直接作答，无需跳转。你也可以在下方输入框继续提问。
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={reviewLoading ? <CircularProgress size={20} color="inherit" /> : <StartReviewIcon />}
                  onClick={handleStartReview}
                  disabled={reviewLoading}
                >
                  {reviewLoading ? 'AI 正在选题…' : '开始复习'}
                </Button>

              </Box>
            </Fade>
          )}

          {plan && plan.items && plan.items.length > 0 && (
            <Paper
              elevation={0}
              sx={{
                mb: 2,
                p: 2,
                borderRadius: 2,
                bgcolor: 'grey.800',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box
                onClick={() => setPlanExpanded((e) => !e)}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
              >
                <Typography variant="subtitle2" fontWeight={600} color="primary.light">
                  做题计划
                </Typography>
                <IconButton size="small">{planExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
              </Box>
              <Collapse in={planExpanded}>
                {plan.summary && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1 }}>
                    {plan.summary}
                  </Typography>
                )}
                <List dense disablePadding>
                  {plan.items.map((item, idx) => {
                    const lastEx = [...messages].reverse().find((m) => m.type === 'exercise' && m.example);
                    const isCurrent = lastEx?.example?.id === item.exampleId;
                    return (
                      <ListItem key={item.exampleId} disablePadding sx={{ py: 0.25 }}>
                        <ListItemText
                          primary={`${idx + 1}. ${item.reason || '—'}`}
                          primaryTypographyProps={{
                            variant: 'body2',
                            color: isCurrent ? 'primary.light' : 'text.secondary',
                            fontWeight: isCurrent ? 600 : 400,
                          }}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </Collapse>
            </Paper>
          )}

          <Stack spacing={2}>
            {messages.map((msg, idx) => {
              const nextMsg = messages[idx + 1];
              const isIntroThenExercise = msg.type === 'text' && msg.role === 'assistant' && nextMsg?.type === 'exercise' && nextMsg?.example;
              const isExerciseAfterIntro = msg.type === 'exercise' && messages[idx - 1]?.type === 'text' && messages[idx - 1]?.role === 'assistant';
              if (isExerciseAfterIntro) return null;

              return (
                <Stack
                  key={msg.id}
                  direction="row"
                  spacing={1.5}
                  justifyContent={msg.role === 'user' ? 'flex-end' : 'flex-start'}
                  alignItems="flex-start"
                >
                  {msg.role === 'assistant' && (msg.type !== 'exercise' || isIntroThenExercise) && (
                    <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36 }}>
                      <SparkleIcon sx={{ fontSize: 20 }} />
                    </Avatar>
                  )}
                  {msg.type === 'text' && !isIntroThenExercise && (
                    <Paper
                      elevation={0}
                      sx={{
                        maxWidth: msg.role === 'assistant' ? '90%' : '75%',
                        minWidth: msg.role === 'assistant' ? 280 : undefined,
                        p: 2,
                        borderRadius: msg.role === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                        bgcolor: msg.role === 'user' ? 'primary.main' : 'grey.900',
                        color: msg.role === 'user' ? 'primary.contrastText' : 'text.primary',
                      }}
                    >
                      {msg.role === 'assistant' ? (
                        <Box sx={{ fontSize: '0.9375rem', lineHeight: 1.6 }}>
                          <MarkdownRenderer content={msg.content || ''} />
                        </Box>
                      ) : (
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{msg.content}</Typography>
                      )}
                    </Paper>
                  )}
                  {isIntroThenExercise && (
                    <Paper
                      elevation={0}
                      sx={{
                        width: '100%',
                        maxWidth: 900,
                        overflow: 'hidden',
                        bgcolor: 'grey.900',
                        borderRadius: '18px 18px 18px 4px',
                      }}
                    >
                      <Box sx={{ p: 2, pb: 0 }}>
                        <Box sx={{ fontSize: '0.9375rem', lineHeight: 1.6 }}>
                          <MarkdownRenderer content={msg.content || ''} />
                        </Box>
                      </Box>
                      <Box sx={{ px: 2, pb: 2 }}>
                        <ExerciseContent
                          example={nextMsg.example}
                          onAnswerSubmitted={(answer, result) => handleAnswerSubmitted(nextMsg.example, answer, result)}
                          embedded
                        />
                      </Box>
                    </Paper>
                  )}
                  {msg.type === 'exercise' && msg.example && !(messages[idx - 1]?.type === 'text' && messages[idx - 1]?.role === 'assistant') && (
                    <Paper
                      elevation={0}
                      sx={{
                        width: '100%',
                        maxWidth: 900,
                        overflow: 'hidden',
                        bgcolor: 'grey.900',
                        borderRadius: '18px 18px 18px 4px',
                      }}
                    >
                      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          本节题目 · {msg.sectionTitle || '未分类'}
                        </Typography>
                      </Box>
                      <Box sx={{ p: 2 }}>
                        <ExerciseContent
                          example={msg.example}
                          onAnswerSubmitted={(answer, result) => handleAnswerSubmitted(msg.example, answer, result)}
                          embedded
                        />
                      </Box>
                    </Paper>
                  )}
                  {msg.role === 'user' && (
                    <Avatar sx={{ bgcolor: 'secondary.main', width: 36, height: 36 }}>
                      <Typography variant="caption">我</Typography>
                    </Avatar>
                  )}
                </Stack>
              );
            })}
            {streamingContent && (
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36 }}>
                  <SparkleIcon sx={{ fontSize: 20 }} />
                </Avatar>
                <Paper elevation={0} sx={{ maxWidth: '90%', minWidth: 280, p: 2, borderRadius: '18px 18px 18px 4px', bgcolor: 'grey.900' }}>
                  <Box sx={{ fontSize: '0.9375rem', lineHeight: 1.6 }}>
                    <MarkdownRenderer content={streamingContent} />
                  </Box>
                </Paper>
              </Stack>
            )}
            {reviewLoading && (
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36 }}>
                  <SparkleIcon sx={{ fontSize: 20 }} />
                </Avatar>
                <Paper elevation={0} sx={{ p: 2, borderRadius: '18px 18px 18px 4px', bgcolor: 'grey.900' }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <CircularProgress size={20} thickness={4} />
                    <Typography variant="body2" color="text.secondary">
                      AI 正在根据你的学习状态选题…
                    </Typography>
                  </Stack>
                </Paper>
              </Stack>
            )}
          </Stack>
          <div ref={messagesEndRef} />
        </Box>

        <Box sx={{ flexShrink: 0, p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
          <Stack direction="row" spacing={1} alignItems="flex-end" flexWrap="wrap">
            <Button
              variant="outlined"
              startIcon={reviewLoading ? <CircularProgress size={18} color="inherit" /> : <StartReviewIcon />}
              onClick={handleStartReview}
              disabled={reviewLoading}
              sx={{ flexShrink: 0 }}
            >
              {reviewLoading ? 'AI 正在选题…' : '开始复习'}
            </Button>
            <TextField
              fullWidth
              size="medium"
              placeholder={
                (() => {
                  const last = [...messages].reverse().find((m) => m.type === 'exercise' && m.example);
                  const qt = last?.example?.questionType;
                  if (qt === 'short_answer') return '输入你的答案并发送';
                  if (qt === 'proof') return '输入你的证明并发送';
                  return '输入问题，继续聊天…';
                })()
              }
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={sending}
              multiline
              maxRows={3}
              variant="outlined"
              sx={{
                minWidth: 200,
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: 'action.hover',
                  color: '#000000',
                  '& .MuiInputBase-input': { color: '#000000' },
                },
              }}
            />
            <IconButton
              onClick={handleSendChat}
              disabled={sending || !inputMessage.trim()}
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                width: 48,
                height: 48,
                '&:hover': { bgcolor: 'primary.dark' },
                '&.Mui-disabled': { bgcolor: 'grey.700', color: 'grey.500' },
              }}
            >
              <SendIcon />
            </IconButton>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}

export default NotebookReviewPage;
